<template>
  <BaseVeeForm :action="updateProduct">
    <template #default="formProps">
      <BaseCard>
        <BaseCardBody clear-padding>
          <div class="p-8 space-y-6">
            <div class="max-w-lg space-y-6">
              <BaseFormTextFieldWithValidator
                v-model="productData.name"
                :disabled="formProps.isPending"
                v-bind="productNameInputProps"
              />
              <BaseFormTextareaFieldWithValidator
                v-model="productData.description"
                :disabled="formProps.isPending"
                v-bind="productDescriptionInputProps"
              />
              <BaseFormTextareaFieldWithValidator
                v-model="productData.supplementary_description"
                :disabled="formProps.isPending"
                v-bind="productSupplementaryDescriptionInputProps"
              />
              <UpdateCoverForm :product-id="product.id" :cover="cover" />
              <ValidationProvider
                name="account types"
                vid="account_types"
                v-slot="{ errors, failed }"
                slim
              >
                <div class="space-y-2">
                  <BaseFormLabel for="account_types">Account types availability</BaseFormLabel>
                  <VSelect
                    v-model="productData.account_types"
                    :options="accountTypeOptions"
                    :reduce="(option) => option.value"
                    :disabled="formProps.isPending"
                    multiple
                    label="label"
                  />
                  <p v-if="failed" class="msg error">
                    {{ errors[0] }}
                  </p>
                </div>
              </ValidationProvider>
              <ValidationProvider
                name="categories"
                vid="categories"
                v-slot="{ errors, failed }"
                slim
              >
                <div class="space-y-2">
                  <BaseFormLabel for="categories">Categories</BaseFormLabel>
                  <VSelect
                    v-model="productData.categories"
                    :options="categoryOptions"
                    :reduce="(option) => option.value"
                    :disabled="formProps.isPending"
                    multiple
                    label="label"
                  />
                  <p v-if="failed" class="msg error">
                    {{ errors[0] }}
                  </p>
                </div>
              </ValidationProvider>
              <ValidationProvider name="tags" vid="tags" v-slot="{ errors, failed }" slim>
                <div class="space-y-2">
                  <BaseFormLabel for="tags">Tags</BaseFormLabel>
                  <VSelect
                    v-model="productData.tags"
                    :options="tagOptions"
                    :reduce="(option) => option.value"
                    :disabled="formProps.isPending"
                    multiple
                    label="label"
                  />
                  <p v-if="failed" class="msg error">
                    {{ errors[0] }}
                  </p>
                </div>
              </ValidationProvider>
            </div>
            <div class="space-y-2">
              <BaseFormLabel>Features</BaseFormLabel>
              <ProductFeaturesManager
                :key="product.updated_at"
                :feature-options="featureOptions"
                :feature-options-value="productData.feature_options"
                :feature-permission-options-value="productData.feature_permission_options"
                @update:feature-options-value="productData.feature_options = $event"
                @update:feature-permission-options-value="
                  productData.feature_permission_options = $event
                "
              />
            </div>
            <BaseButton primary :disabled="formProps.isPending || formProps.invalid"
              >Update product</BaseButton
            >
          </div>
        </BaseCardBody>
      </BaseCard>
    </template>
  </BaseVeeForm>
</template>

<script>
import { membershipProductsClient } from '@/api/clients/admin';
import UpdateCoverForm from './UpdateCoverForm.vue';
import ProductFeaturesManager from '@/components/ProductFeaturesManager/ProductFeaturesManager.vue';

export default {
  components: {
    UpdateCoverForm,
    ProductFeaturesManager,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    categoryOptions: {
      type: Array,
      default: () => [],
    },
    tagOptions: {
      type: Array,
      default: () => [],
    },
    accountTypeOptions: {
      type: Array,
      default: () => [],
    },
    featureOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      productData: {
        name: '',
        description: '',
        supplementary_description: '',
        categories: [],
        tags: [],
        account_types: [],
        feature_options: [
          //
        ],
        feature_permission_options: [
          //
        ],
      },
    };
  },
  computed: {
    cover() {
      return (this.product?.media || []).find((item) => item.collection === 'images') || null;
    },
    productNameInputProps() {
      return {
        validator: {
          name: 'name',
          vid: 'name',
          rules: { required: true },
        },
        field: {
          id: 'product-name',
          label: 'Product name',
        },
      };
    },
    productDescriptionInputProps() {
      return {
        validator: {
          name: 'description',
          vid: 'description',
          rules: { required: false },
        },
        field: {
          id: 'product-description',
          label: 'Description',
        },
      };
    },
    productSupplementaryDescriptionInputProps() {
      return {
        validator: {
          name: 'supplementary description',
          vid: 'supplementary_description',
          rules: { required: false },
        },
        field: {
          id: 'product-supplementary-description',
          label: 'Supplementary description',
          rows: 5,
        },
      };
    },
  },
  watch: {
    product: {
      deep: true,
      handler() {
        this.setProductData();
      },
    },
  },
  created() {
    this.setProductData();
  },
  methods: {
    setProductData() {
      this.productData.name = this.product.name;
      this.productData.description = this.product.description;
      this.productData.supplementary_description = this.product.supplementary_description;
      this.productData.categories = this.product.categories.map((category) => category.id);
      this.productData.tags = this.product.tags.map((tag) => tag.id);
      this.productData.feature_options = this.product?.feature_options || [];
      this.productData.feature_permission_options = this.product?.feature_permission_options || [];
      this.productData.account_types = this.product?.account_types || [];
    },
    updateProduct() {
      return membershipProductsClient.update(this.product.id, this.productData).then(() => {
        this.$emit('updated');
      });
    },
  },
};
</script>
